import React, { useEffect, useRef, useState } from "react";
import { s3baseUrl } from "src/config/config";
import { useSnackbar } from "notistack";
import {
  community_feed_action,
  delete_feed_action,
} from "src/DAL/Community/Community";
import { delete_comment_api } from "src/DAL/Community/Comments";
import UserInfoCard from "src/components/CommunityFeed/UserInfoCard";
import ActionsCount from "src/components/CommunityFeed/ActionsCount";
import HandleAction from "src/components/CommunityFeed/HandleAction";
import { htmlDecode } from "src/utils/convertHtml";
import FeedImagesList from "src/components/CommunityFeed/FeedImagesList";
import ShowAllLikes from "src/components/CommunityFeed/ShowAllLikes";
import AddComment from "./AddComment";
import FeedComments from "src/components/CommunityFeed/FeedComments";
import SingleComment from "src/components/CommunityFeed/SingleComment";
import ReactVideoPlayerPosts from "src/components/ReactPlayers/ReactVideoPlayerPosts";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import SendCoins from "src/components/CommunityFeed/SendCoins";
import ShowCoinsMembers from "src/components/CommunityFeed/ShowCoinsMembers";
import FeedMoreMenu from "src/components/CommunityFeed/FeedMoreMenu";
import GrRichText from "src/design-system/components/GrRichText";
import {
  memberAsLiker,
  withLiker,
  withoutLiker,
} from "src/components/CommunityFeed/feedLikers";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import Box from "@mui/material/Box";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import { gr } from "src/design-system/tokens";
import { feedPostSx } from "src/components/CommunityFeed/feedPostSx";

// One feed post, one look (feedPostSx, shared with the post popup). On the community feed it is its own
// card (padding 20, 16 on phones); on the dashboard it sits directly in the dashboard's "Community Feed"
// GrCard. Photos and video run edge to edge: `--feed-post-pad` is the padding of the card around the post.
const PHONE = "@media (max-width: 599.95px)";
const cardSx = {
  ...feedPostSx,
  "--feed-post-pad": gr.space[5],
  padding: "var(--feed-post-pad)",
  [PHONE]: { "--feed-post-pad": gr.space[4], padding: "var(--feed-post-pad)" },
};
// The dashboard GrCard's own padding (GrCard "md": 24, 20 on phones).
const dashboardSx = {
  ...feedPostSx,
  "--feed-post-pad": gr.space[6],
  [PHONE]: { "--feed-post-pad": gr.space[5] },
};
const headerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
};
// Edge-to-edge photos: square corners, like the video.
const photoGridSx = { borderRadius: 0 };

const DOUBLE_TAP_MS = 600;
const POST_TEXT_LINES = 4;
// Post text keeps the member's own line breaks.
const postTextSx = { whiteSpace: "pre-wrap" };

function PostCard({
  feeds_list,
  getFeedsListingOnDelete,
  handleShowDetails,
  handleSelectedImage,
  handleFeedEdit,
  handleUpdateSpecificFeed,
  handleUpdateShowAll,
  openDetailModal,
  dashboard,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo } = useContentSetting();
  const [showAddComment, setShowAddComment] = useState(false);
  const [openLikesModal, setOpenLikesModal] = useState(false);
  const [openCoinsModal, setOpenCoinsModal] = useState(false);
  const [sendCoins, setSendCoins] = useState(false);
  const [showTab, setShowTab] = useState(0);
  const [isLiked, setIsLiked] = useState(true);
  const [commetMessage, setCommetMessage] = useState({});
  const [topLikedUser, setTopLikedUser] = useState([]);
  const [topCoinedUser, setTopCoinedUser] = useState([]);
  const [likeCount, setLikeCount] = useState(0);
  const [coinsCount, setcoinsCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDeleteComment, setOpenDeleteComment] = useState(false);
  const [formAction, setFormAction] = useState("ADD");
  const [selectedComment, setSelectedComment] = useState({});
  const [commentSelected, setCommentSelected] = useState({});
  // The heart and the count change at once (the API answer only confirms them); a second tap while
  // the first is still saving is ignored, so a double tap cannot like and unlike in a race.
  const likeSaving = useRef(false);
  const likeTappedAt = useRef(0);
  const handleFeedAction = async (id, action) => {
    if (likeSaving.current) return;
    likeSaving.current = true;
    likeTappedAt.current = Date.now();
    const like = action === "feedlike";
    const me = memberAsLiker(userInfo);
    const likersBefore = topLikedUser;
    setIsLiked(like);
    setLikeCount((count) => Math.max(0, count + (like ? 1 : -1)));
    setTopLikedUser((likers) =>
      like ? withLiker(likers, me) : withoutLiker(likers, me)
    );
    const result = await community_feed_action(id, like ? "like" : "unlike");
    likeSaving.current = false;
    if (result.code === 200) {
      setLikeCount(result.feed.liked_by?.length);
      handleUpdateSpecificFeed(id);
    } else {
      setIsLiked(!like);
      setLikeCount((count) => Math.max(0, count + (like ? -1 : 1)));
      setTopLikedUser(likersBefore);
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleAgree = async () => {
    setOpenDelete(false);
    const result = await delete_feed_action(feeds_list._id);
    if (result.code === 200) {
      enqueueSnackbar("Post deleted successfully", { variant: "success" });
      getFeedsListingOnDelete(feeds_list._id);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handlePostDetail = (post_id) => {
    handleShowDetails(feeds_list);
  };

  const handleClickImage = (event, index, path) => {
    handleSelectedImage(feeds_list, index);
  };

  const handleViewAllComments = (value) => {
    handleUpdateShowAll(feeds_list._id, value);
  };
  const handleCloseSendCoins = () => {
    setSendCoins(false);
  };
  // The first like makes the counts row appear above the buttons; the second tap of a quick double tap
  // can land on it, so it does not open the likes list right after a like.
  const handleOpenLikedBox = () => {
    if (Date.now() - likeTappedAt.current < DOUBLE_TAP_MS) return;
    setOpenLikesModal(true);
    setShowTab(0);
  };
  const handleOpenCoinsBox = () => {
    setOpenCoinsModal(true);
    // setShowTab(0);
  };

  const handleCloseSimpleBox = () => {
    setOpenLikesModal(false);
    setShowTab();
  };
  const handleCloseCoinBox = () => {
    setOpenCoinsModal(false);
    setShowTab();
  };

  const handleAgreeDelete = (value) => {
    setOpenDelete(true);
  };

  const handleCommentEdit = (value) => {
    setCommentSelected(value);
    setFormAction("EDIT");
    setShowAddComment(true);
  };

  const handleCommentDelete = (selected_comment) => {
    setOpenDeleteComment(true);
    setSelectedComment(selected_comment);
  };

  const handleDelete = async () => {
    setOpenDeleteComment(false);
    const result = await delete_comment_api(selectedComment._id);
    if (result.code === 200) {
      handleUpdateSpecificFeed(feeds_list._id);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    setIsLiked(feeds_list?.liked_by_me);
    setCommetMessage(feeds_list.comments[0]);
    setTopLikedUser(feeds_list.liked_by);
    setTopCoinedUser(feeds_list.feed_points);
    setLikeCount(feeds_list.liked_by.length);
    setcoinsCount(feeds_list.feed_points.length);
    setCommentCount(feeds_list.total_comments);
  }, [feeds_list]);

  const post = (
    <>
      <Box sx={headerSx}>
        <UserInfoCard
          profile_image={s3baseUrl + feeds_list.created_by?.profile_image}
          user_name={htmlDecode(feeds_list.created_by?.name)}
          avatar_char={feeds_list.created_by?.name}
          date={feeds_list?.createdAt}
          type={feeds_list.created_by?.user_type}
        />
        {!dashboard && feeds_list.is_self && (
          <FeedMoreMenu
            onEdit={() => handleFeedEdit(feeds_list)}
            onDelete={() => handleAgreeDelete(feeds_list)}
          />
        )}
      </Box>
      {feeds_list.description ? (
        // Long text shows four lines with "View more" (opens it here, "View less" folds it); a click on
        // the text itself still opens the post, as before.
        <div
          className="post-description"
          onClick={(event) => {
            if (event.target.closest("button")) return;
            handlePostDetail(feeds_list._id);
          }}
        >
          <GrRichText
            html={feeds_list.description}
            collapsedLines={POST_TEXT_LINES}
            sx={postTextSx}
          />
        </div>
      ) : null}
      {feeds_list.feed_type === "image" && feeds_list.image && (
        <div className="feed-media">
          <FeedImagesList
            feed_images={feeds_list.image}
            handleClickImage={handleClickImage}
            sx={photoGridSx}
          />
        </div>
      )}
      {feeds_list.feed_type === "video" && feeds_list.video_url && (
        <div className="feed-media">
          <ReactVideoPlayerPosts url={feeds_list.video_url} />
        </div>
      )}

      <ActionsCount
        handleOpenLikedBox={handleOpenLikedBox}
        handleOpenCoinsBox={handleOpenCoinsBox}
        likeCount={likeCount}
        coinsCount={coinsCount}
        commentCount={commentCount}
        topLikedUser={topLikedUser}
        topCoinedUser={topCoinedUser}
      />
      <hr />
      <HandleAction
        isLiked={isLiked}
        handleFeedAction={handleFeedAction}
        setShowAddComment={setShowAddComment}
        showAddComment={showAddComment}
        feed_id={feeds_list._id}
        setSendCoins={setSendCoins}
        sendCoins={sendCoins}
        feeds_list={feeds_list}
      />
      <hr />
      <div className="profile-comments">
        {commentCount > 0 &&
          (feeds_list.is_show_all ? (
            <FeedComments feed_id={feeds_list._id} />
          ) : (
            <SingleComment
              comment={commetMessage}
              handleCommentEdit={handleCommentEdit}
              handleCommentDelete={handleCommentDelete}
              handleUpdateSpecificFeed={handleUpdateSpecificFeed}
              setShowAddComment={setShowAddComment}
              showAddComment={showAddComment}
              openDetailModal={openDetailModal}
              handlePostDetail={handlePostDetail}
            />
          ))}
        {commentCount > 1 && (
          <div className="view-comment">
            {!feeds_list.is_show_all ? (
              <GrButton
                variant="link"
                size="sm"
                onClick={() => handlePostDetail(feeds_list._id)}
              >
                View more comments
              </GrButton>
            ) : (
              <GrButton
                variant="link"
                size="sm"
                onClick={() => handleViewAllComments(false)}
              >
                View less comments
              </GrButton>
            )}
          </div>
        )}
        {showAddComment && !feeds_list.is_show_all && (
          <AddComment
            post_id={feeds_list._id}
            formAction={formAction}
            feedsData={feeds_list}
            setFormAction={setFormAction}
            setShowAddComment={setShowAddComment}
            handleUpdateSpecificFeed={handleUpdateSpecificFeed}
            commentSelected={commentSelected}
            setCommentSelected={setCommentSelected}
          />
        )}
      </div>
    </>
  );

  return (
    <>
      {dashboard ? (
        <Box component="article" sx={dashboardSx}>
          {post}
        </Box>
      ) : (
        <GrCard as="article" sx={cardSx}>
          {post}
        </GrCard>
      )}
      {openLikesModal === true && (
        <ShowAllLikes
          openLikesModal={openLikesModal}
          setOpenLikesModal={setOpenLikesModal}
          handleCloseSimpleBox={handleCloseSimpleBox}
          showTab={showTab}
          post_id={feeds_list._id}
          feeds_list={feeds_list}
          feeds_type="general"
        />
      )}
      {openCoinsModal === true && (
        <ShowCoinsMembers
          openLikesModal={openCoinsModal}
          setOpenLikesModal={setOpenCoinsModal}
          handleCloseSimpleBox={handleCloseCoinBox}
          showTab={0}
          post_id={feeds_list._id}
          feeds_list={feeds_list}
          feeds_type="general"
        />
      )}
      {sendCoins === true && (
        <SendCoins
          sendCoins={sendCoins}
          setOpenLikesModal={setSendCoins}
          handleCloseSimpleBox={handleCloseSendCoins}
          showTab={0}
          post_id={feeds_list._id}
          feeds_list={feeds_list}
          handleUpdateSpecificFeed={handleUpdateSpecificFeed}
          feeds_type="general"
        />
      )}
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this post?"}
        handleAgree={handleAgree}
      />
      <CustomConfirmation
        open={openDeleteComment}
        setOpen={setOpenDeleteComment}
        title={"Are you sure you want to delete this comment?"}
        handleAgree={handleDelete}
      />
    </>
  );
}

export default PostCard;
